import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {
    IStillImageBackgroundConfig,
    StillImageBackground
} from "@nolimitcity/slot-game/bin/game/background/StillImageBackground";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Power2, Power4, Timeline, TimelineLite, TimelineMax, TweenLite, TweenMax} from "gsap";
import {ReelAttentionEvent} from "../reelstoppresentation/reelattention/TractorBeamReelAttentionController";
import {BasicStateReelSymbol} from "../symbols/BasicStateReelSymbol";
import {TractorBeamGameAssets} from "../TractorBeamGameAssets";
import {TractorBeamGameConfig} from "../TractorBeamGameConfig";
import {TractorBeamSoundConfig} from "../TractorBeamSoundConfig";
import {UFOView} from "./ufo/UFOView";

export interface ITractorBeamStillImageBackgroundConfig extends IStillImageBackgroundConfig {
    readonly positions:[number, number][];
}

export class TractorBeamBackgroundEvent {
    public static readonly TOGGLE_COMMAND_CENTER:string = "tractorBeamBackgroundEvent_toggleCommandCenter";
}

export interface IColorHash {
    [details:string]:number;
}

export class TractorBeamBackground extends StillImageBackground {

    private _foregroundLayer:ScalableLayer;
    protected readonly _positions:[number, number][];
    private _ReelSprites:{
        "beam":TimelineSprite
    }[];

    private _reelBGLayer:ScalableLayer;
    private _reelFGContainer:PIXI.Container;
    private _beamTimelines:Timeline [] = [];
    private _fadeLayer:ScalableLayer;


    // base beam and tint/color effects
    private _baseBeams:TimelineSprite [] = [];
    private _activeBeamIndexes:number [] = [];

    // Tints
    private _tintMap:IColorHash = {};
    private _hueTimeline:Timeline;

    // BG Zoom & Pane
    private _foregroundSprite:PIXI.Sprite;
    private _fsForegroundSprite:PIXI.Sprite;
    private _fsForegroundSprite2:PIXI.Sprite;
    private _zoomTl:Timeline | null;

    private _wiggleSprite:PIXI.Sprite;

    // UFO
    private _ufoView:UFOView;

    // Clouds & Flare
    private _cloudLeft:TimelineSprite;
    private _cloudRight:TimelineSprite;
    private _flare:PIXI.Sprite;

    private _fgY:number;
    private _fgYPortrait:number;

    private _bgY:number;
    private _bgYPortrait:number;

    private _isPortrait:boolean = false;

    private _activeBeam:number = -1;


    constructor(config:ITractorBeamStillImageBackgroundConfig) {
        super(config);
        this._ReelSprites = [];
        this._positions = config.positions;

        this._reelBGLayer = StageManager.getLayer(TractorBeamGameConfig.instance.LAYERS.REEL_BACKGROUND.name);
        this._foregroundLayer = StageManager.getLayer(TractorBeamGameConfig.instance.LAYERS.FOREGROUND.name);
        this._fadeLayer = StageManager.getLayer(TractorBeamGameConfig.instance.LAYERS.FADE_WHITE.name);

        this.populateTintMap();
        // UFO
        this._ufoView = new UFOView();
    }

    protected onResize(resizeData:IResizeData):void {
        this._backgroundSprite.x = -200;
        this._foregroundSprite.x = -200;

        if(resizeData.orientation === Orientation.PORTRAIT) {
            this._positions.forEach((pos, index) => {
                if(this._ReelSprites[index].beam) {
                    this._ReelSprites[index].beam.position.y = pos[1] + 64;
                }
                this._baseBeams[index].position.y = pos[1] + 230;
            });

            if(this._currentMode === "FREESPIN") {
                this._backgroundSprite.y = -400;
                this._fsForegroundSprite2.visible = true;
            } else {
                this._backgroundSprite.y = -400;
                this._fsForegroundSprite2.visible = false;
            }
            this._foregroundSprite.y = 150;
            this._fsForegroundSprite.position.y = 630;
            this._isPortrait = true;
        } else {
            this._positions.forEach((pos, index) => {
                if(this._ReelSprites[index].beam) {
                    this._ReelSprites[index].beam.position.y = pos[1] - 40;
                }
                this._baseBeams[index].position.y = pos[1] + 125;
            });

            this._backgroundSprite.y = -400;
            this._foregroundSprite.y = 50;
            this._fsForegroundSprite.position.y = 500;

            if(this._currentMode !== "FREESPIN") {
                this._fsForegroundSprite2.visible = false;
            }

            this._isPortrait = false;
        }
    }


    /**
     * Function to add class event listeners.
     */
    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, ReelAttentionEvent.REEL_ATTENTION_PLAYING, (event:GameEvent) => this.addColourTint(event.params[0], undefined, event.params[1], event.params[2]));
        EventHandler.addEventListener(this, ReelAttentionEvent.REEL_ATTENTION_STOPPING, (event:GameEvent) => this.removeColourTint(event.params[0]));
    }

    public setBg():void {
        this._backgroundSprite.y = (!this._isPortrait) ? -380 : -380;
    }

    public zoomAndPane(up:boolean = true, restore:boolean = false, data?:ParsedServerData | null):Timeline {
        const timeline:Timeline = new TimelineMax();

        if(restore) {
            timeline.add(() => {
                this._backgroundSprite.y = (!this._isPortrait) ? -400 : -400;
                this._foregroundSprite.y = (!this._isPortrait) ? 50 : 150;
                this._foregroundSprite.visible = true;
            });
            if(data && data.mode === "FREESPIN") {
                timeline.add(() => {
                    this._backgroundSprite.y = -380;
                    //this._backgroundSprite.y = -960;
                    this._fsForegroundSprite.visible = true;
                    this._fsForegroundSprite2.visible = true;
                    this._foregroundSprite.visible = false;
                });
            }
        } else {
            if(!up) {
                this._backgroundSprite.y = (!this._isPortrait) ? -380 : -380;
            }

            timeline.add([
                (up) ? () => {
                    this._ufoView.intoFsAnimation(this._wiggleSprite);
                    SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.FS_INTRO);
                    TweenMax.to(this._foregroundSprite, 0.1, {x : "+=10", yoyo : true, repeat : 5});
                    //TweenMax.to(this._foregroundSprite, 0.15, {y:"+=15", yoyo:true, repeat:5});

                    TweenMax.to(this._backgroundSprite, 0.1, {
                        x : "+=2",
                        yoyo : true,
                        repeat : 5,
                        ease : Power2.easeIn
                    });
                    TweenMax.to(this._backgroundSprite, 0.15, {
                        y : "+=25",
                        yoyo : true,
                        repeat : 5,
                        ease : Power2.easeIn
                    });
                } : () => {},
                TweenMax.to(this._backgroundSprite, 1, {
                    y : (up) ? (!this._isPortrait) ? -380 : -380 : (!this._isPortrait) ? -400 : -400
                }),
                TweenMax.to(this._foregroundSprite, 1, {
                    y : (up) ? (!this._isPortrait) ? 20 : 20 : (!this._isPortrait) ? 50 : 150
                })
            ]);
            timeline.add([
                (up) ? () => TweenMax.to(this._foregroundSprite, 0.15, {
                    x : "+=20",
                    yoyo : true,
                    repeat : 15
                }) : () => {},
                () => TweenMax.to(this._backgroundSprite, 1, {
                    y : (up) ? (!this._isPortrait) ? -380 : -380 : (!this._isPortrait) ? -400 : -400,
                    ease : Power4.easeIn

                }),
                () => TweenMax.to(this._foregroundSprite, 1.75, {
                    y : (up) ? (!this._isPortrait) ? 90 : 190 : (!this._isPortrait) ? 50 : 150,
                    ease : Power4.easeIn
                })
            ]);

        }

        this._zoomTl = timeline;
        return this._zoomTl;
    }

    public onFSIntroComplete():void {

        new TimelineLite({
            'onComplete' : () => {
                this._backgroundSprite.y = -400;
                this._fsForegroundSprite.visible = true;
                this._fsForegroundSprite2.visible = true;
                this._foregroundSprite.visible = false;
            }
        }).add([
            () => this.thrustMe(),
            TweenMax.to(this._backgroundSprite, 1, {
                y : -400,
                ease : Power4.easeOut

            })
            //,
            //() => this._flare.alpha = 1,
            //() => this._flare.scale.set(9, 9)
            /*,
            TweenMax.to(this._foregroundSprite, 1, {
                y: 1000,
                ease: Power4.easeOut
            })*/
        ], 0.3);
        //this._backgroundSprite.y = -960;

    }

    /**
     * Function to init/create animation when class is initiated/loaded.
     */
    protected initAnimations():void {
        super.initAnimations();

        this._wiggleSprite = new PIXI.Sprite();
        this._wiggleSprite.x = 660;
        this._wiggleSprite.pivot = new PIXI.Point(330);
        this._wiggleSprite.anchor.set(0.5, 0.5);
        //this._wiggleSprite.rotation = TractorBeamHelper.radians(45);

        // TODO: Background positions need to be handled in portrait as well
        for(let i:number = 0; i < this._positions.length; i++) {
            const reelForegroundContainer = new PIXI.Container();
            this._reelFGContainer = reelForegroundContainer;

            // TODO: rename foreground -> background, again...
            const foreground:TimelineSprite = new TimelineSprite(GameResources.getTextures(TractorBeamGameAssets.MAINGAME_REEL_BACKGROUND));
            foreground.position.set((this._positions[i][0] + 10) - 330, this._positions[i][1] - 40);
            foreground.anchor.set(0.25, 0);
            if(!(i % 2 === 0)) {
                foreground.scale.set(-1, 1);
                foreground.anchor.set(0.75, 0);
                foreground.position.set((this._positions[i][0] + 15) - 330, this._positions[i][1] - 40);
            }

            //foreground.setFrame((i) * 5);
            //foreground.playLoop();


            reelForegroundContainer.addChild(foreground);
            this._ReelSprites.push({
                "beam" : foreground
            });

            //this._reelBGLayer.addChild(reelForegroundContainer);

            this._wiggleSprite.addChild(foreground);

        }

        this._reelBGLayer.addChild(this._wiggleSprite);

        const tl:TimelineLite = new TimelineLite();

        this._ReelSprites.forEach((rSprite:any) => {
            tl.add(() => rSprite.beam.playLoop(), "+=0.35");
        });


        // Base beam setup
        [0, 1, 2, 3, 4].forEach((reelIndex:number) => {
            const pos:number [] = Helper.getSymbolPositions(reelIndex, 0);
            this._baseBeams.push(this.createBaseBeamAnimation(new PIXI.Point(pos[0], pos[1])));
        });

        const foregroundSprite:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(TractorBeamGameAssets.FORE_GROUND)[0]);
        foregroundSprite.position.set(-200, 50);
        foregroundSprite.visible = false;
        this._foregroundLayer.addChild(foregroundSprite);
        this._foregroundSprite = foregroundSprite;

        const fsForegroundSprite:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(TractorBeamGameAssets.FS_FORE_GROUND)[0]);
        fsForegroundSprite.position.set(-200, 630);
        fsForegroundSprite.visible = false;
        this._foregroundLayer.addChild(fsForegroundSprite);
        this._fsForegroundSprite = fsForegroundSprite;

        const fsForegroundSprite2:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(TractorBeamGameAssets.FS_FORE_GROUND)[0]);
        fsForegroundSprite2.position.set(1050, 1400);
        fsForegroundSprite2.anchor.set(0.5, 0.5);
        fsForegroundSprite2.visible = false;
        fsForegroundSprite2.scale.set(-1, 1);
        this._foregroundLayer.addChild(fsForegroundSprite2);
        this._fsForegroundSprite2 = fsForegroundSprite2;


        this._flare = new PIXI.Sprite(GameResources.getTextures("bwbg")[0]);
        this._flare.blendMode = PIXI.BLEND_MODES.ADD;
        this._flare.anchor.set(0.5, 0.38);
        this._flare.alpha = 0;
        this._flare.position.set(670, 300);
        this._fadeLayer.addChild(this._flare);

        this._cloudRight = this.createThrustCloud();
        this._fadeLayer.addChild(this._cloudRight);

        this._cloudLeft = this.createThrustCloud(true);
        this._fadeLayer.addChild(this._cloudLeft);

        //this.tmpDebugHandler();
    }

    private playBeamUpOnAllSyms():Timeline {
        const tl:TimelineLite = new TimelineLite();

        [0, 1, 2, 3, 4].forEach((reelId:number) => {
            [0, 1, 2].forEach((symId:number) => {
                const symbol:BasicStateReelSymbol | null = <BasicStateReelSymbol>Reels.getSymbol(reelId, symId);

                if(symbol && ["S", "W"].indexOf(symbol.symName) < 0) {
                    const symName = symbol.symName;
                    symbol.playSpineAnimation(symName, symName + "-beamOut", false, 0.75, (trackEntry:PIXI.spine.core.TrackEntry) => {
                        //debugger;
                        if(trackEntry.animation.name === symName + "-beamOut") {
                            symbol.playSpineAnimation(symName, symName + "-idle", true, 0.3);
                        }
                    })
                }
            });
        });

        return tl;
    }

    private createThrustCloud(left:boolean = false):TimelineSprite {
        const tls:TimelineSprite = new TimelineSprite(GameResources.getTextures("thrusterSmoke"));
        tls.anchor.set(0.5, 0.5);
        tls.hide();

        if(left) {
            tls.scale.set(-1, 1);
            tls.position.set(50, 600);
        } else {

            tls.position.set(1140, 600);
        }

        return tls;
    }

    public thrustMe():Timeline {
        const tl:TimelineLite = new TimelineLite({
            "onComplete" : () => {
                this._flare.scale.set(11, 11);
                this._flare.alpha = 1;
                new TimelineLite({
                    "onComplete" : () => {
                        this._flare.scale.set(0, 0);
                        this._flare.alpha = 0;
                    }
                }).add(TweenLite.to(this._flare, 0.3, {alpha : 0}));
            }
        });

        tl.add([
            () => this.playBeamUpOnAllSyms(),
            () => this._cloudLeft.getAnimationAutoShowHide(),
            () => this._cloudRight.getAnimationAutoShowHide(),
            TweenMax.to(this._cloudLeft.scale, this._cloudLeft.totalDuration, {
                x : -6,
                y : 6
            }),
            TweenMax.to(this._cloudRight.scale, this._cloudRight.totalDuration, {
                x : 6,
                y : 6
            })
        ]);
        tl.add([
            () => this._flare.scale.set(0, 0),
            () => this._flare.alpha = 1,
            TweenLite.to(this._flare.scale, this._cloudRight.totalDuration / 2, {
                x : 11,
                y : 11
            })
        ], this._cloudRight.totalDuration - 0.15);
        tl.add(() => {}, this._cloudRight.totalDuration + 0.35);


        return tl;
    }

    private tmpDebugHandler():void {
        const ref = this;
        let up:boolean = true;

        document.addEventListener("keydown", function(e) {
            var keyCode = e.keyCode;
            //console.log("KeyCode: " + keyCode);

            switch(keyCode) {
                case 48:
                    ref.stopIntenseBeam(2);
                    if(ref._zoomTl) {
                        ref._zoomTl.pause();
                        ref._zoomTl.kill();
                    }
                    break; // 0
                case 49:
                    //ref.stopIntenseBeam(2);
                    ref.showAndPlayIntenseBeam(4, "blue");
                    break; // 1
                case 50:
                    //ref.stopIntenseBeam(2);
                    ref.showAndPlayIntenseBeam(4, "red");
                    //ref.showAndPlayIntenseBeam(2, "gold");
                    break; // 2
                case 51:
                    //ref.stopIntenseBeam(2);
                    ref.showAndPlayIntenseBeam(4, "hueRotation");
                    break; // 3
                case 52:
                    //ref.stopIntenseBeam(2);
                    //ref.showAndPlayIntenseBeam(2, "red");
                    break; /// 4
                case 53:
                    //ref.stopIntenseBeam(2);
                    //ref.showAndPlayIntenseBeam(2, "green");
                    break; // 5
                case 54:
                    if(!ref._zoomTl) {
                        console.log(ref._backgroundSprite.y);
                        //console.log(ref.)
                    }
                    if(ref._zoomTl) {
                        ref._zoomTl = null;
                    }
                    ref._fsForegroundSprite.visible = false;
                    ref._foregroundSprite.visible = true;

                    ref.zoomAndPane(up);
                    up = !up;
                    //ref.stopIntenseBeam(2);
                    //ref.showAndPlayIntenseBeam(2, "hueRotation");
                    break; // 6
                case 55:
                    if(!ref._zoomTl) {
                        console.log(ref._backgroundSprite.y);
                        //console.log(ref.)
                    }
                    if(ref._zoomTl) {
                        ref._zoomTl = null;
                    }
                    ref.zoomAndPane(up).eventCallback('onComplete', () => {
                        if(up) {
                            ref.onFSIntroComplete();
                            up = !up;
                        }
                    });
                    //ref.stopIntenseBeam(2);
                    //ref.showAndPlayIntenseBeam(2, "hueRotation");
                    break; // 7
            }
        }, false);
    }

    protected populateTintMap():void {
        this._tintMap["red"] = 0xFF2222;
        this._tintMap["blue"] = 0x5555FF;
        this._tintMap["green"] = 0xBBFFBB;
        this._tintMap["gold"] = 0xFFDF00; //0xEEC600;
        this._tintMap["white"] = 0xFFFFFF;
        this._tintMap["hueRotation"] = 0xEEC600;
    }

    protected createBaseBeamAnimation(position:PIXI.Point = new PIXI.Point(0, 0), flip:boolean = false):TimelineSprite {
        const tls:TimelineSprite = new TimelineSprite(GameResources.getTextures("baseBeam"));

        tls.hide();
        tls.playLoop();
        //tls.tint = this._tintMap["blue"];
        tls.position.set(position.x + 5, position.y - 5);
        //tls.blendMode = PIXI.BLEND_MODES.ADD;
        tls.anchor.set(0.5, 0.245);

        this._foregroundLayer.addChild(tls);
        return tls;
    }

    public showAndPlayIntenseBeam(beamId:number, tintName:string, delay:number = 0):Timeline {
        const timeline:Timeline = new TimelineLite();
        let hueCounter:number = 0;
        const colorMatrix = new PIXI.filters.ColorMatrixFilter();


        if(this._hueTimeline) {
            this._hueTimeline.pause();
            this._hueTimeline.kill();
        }

        if(tintName === "hueRotation") {
            let sub:boolean = true;

            this._baseBeams[beamId].filters = [colorMatrix];
            colorMatrix.hue(0, true);
            colorMatrix.blendMode = PIXI.BLEND_MODES.SCREEN;
            //colorMatrix.saturate(1, true);

            /*this._hueTimeline = new TimelineMax({repeat: -1}).add( // yoyo
                () => {
                    if(hueCounter > 90) { // -165
                        hueCounter = 90;
                        sub = true;
                    }

                    if(hueCounter < 0) {
                        hueCounter = 0;
                        sub = false;
                    }

                    if(sub) {
                        colorMatrix.hue(hueCounter-=15); // +=5
                    }
                    else {
                        colorMatrix.hue(hueCounter+=15); // +=5
                    }
                }, 0.033);*/
        } else {
            this._baseBeams[beamId].filters = [];
        }

        timeline.add([
            () => {
                if(tintName === "hueRotation") {
                    //this._baseBeams[beamId].blendMode = PIXI.BLEND_MODES.SCREEN;

                } else if(tintName === "blue") {
                    //colorMatrix.blendMode = PIXI.BLEND_MODES.SCREEN;
                    this._baseBeams[beamId].blendMode = PIXI.BLEND_MODES.SCREEN;
                    colorMatrix.hue(0, true);
                    this._baseBeams[beamId].filters = [colorMatrix];
                } else if(tintName === "green") {
                    colorMatrix.blendMode = PIXI.BLEND_MODES.SCREEN;
                    this._baseBeams[beamId].blendMode = PIXI.BLEND_MODES.SCREEN;
                } else if(tintName === "red") {
                    colorMatrix.blendMode = PIXI.BLEND_MODES.SCREEN;
                    colorMatrix.hue(145, true);
                    this._baseBeams[beamId].filters = [colorMatrix];
                } else {
                    //colorMatrix.blendMode = PIXI.BLEND_MODES.SCREEN;
                    this._baseBeams[beamId].blendMode = PIXI.BLEND_MODES.SCREEN;
                }
            },
            //() => this._baseBeams[beamId].tint = this._tintMap[tintName],
            () => this._baseBeams[beamId].alpha = 0,
            () => this._baseBeams[beamId].show(),
            () => this._baseBeams[beamId].playLoop(),
            () => this._baseBeams[beamId].show(),
            //() => console.log("HUE: " + hueCounter),
            () => TweenLite.fromTo(this._baseBeams[beamId], 0.2, {
                alpha : 0
            }, {
                alpha : 1
            })
        ], delay);


        return timeline;
    }

    public stopIntenseBeam(beamId:number, delay:number = 0):Timeline {
        const timeline:TimelineLite = new TimelineLite();

        timeline.add(TweenLite.to(
            this._baseBeams[beamId], 0.4, {
                alpha : 0
            }
        ), delay);
        timeline.add(() => {
            this._baseBeams[beamId].alpha = 0;
            this._baseBeams[beamId].hide();
            this._baseBeams[beamId].tint = this._tintMap["white"];
        });

        return timeline;
    }


    protected getStopIntenseBeamTimeline(index:number):Timeline {
        const timeline:Timeline = new TimelineLite();

        return timeline;
    }

    protected onChangeBackground(data:IGameModeChangedData):boolean {
        const bool:boolean = super.onChangeBackground(data);

        if(data.newMode === "NORMAL") {
            this._foregroundSprite.visible = true;
            this._fsForegroundSprite.visible = false;
            this._fsForegroundSprite2.visible = false;
            //this._backgroundSprite.y = -960;
        }

        if(data.newMode === "FREESPIN") {
            this._backgroundSprite.y = -400;
            this._backgroundSprite.x = -200;
            this._foregroundSprite.y = (!this._isPortrait) ? 50 : 150;
        } else {
            this._backgroundSprite.x = -200;
            this._backgroundSprite.y = -380;
        }

        return bool;
    }

    /**
     * Function to change reel beams on near reel spin attention behaviour.
     * @param reelId
     * @param [alpha = 1]
     * @param [stackTriggered = false]
     */
    public addColourTint(reelId:number, alpha:number = 1, stackTriggered:boolean = false, delay:number = 0):void {

        if(stackTriggered && reelId !== this._activeBeam) {
            this.showAndPlayIntenseBeam(reelId, "blue", delay);
            this._activeBeam = reelId;
        } else if(!stackTriggered) {
            this.showAndPlayIntenseBeam(reelId, "red");
            this._activeBeam = -1;
        }

        this._activeBeamIndexes.push(reelId);
    }

    /**
     * Function to reset reel beams on near reel spin attention behaviour.
     */
    public removeColourTint(reelIndex:number = 0):void {
        this._ReelSprites[reelIndex].beam.alpha = 1;
    }

    public resetBeams():void {
        this._beamTimelines.forEach((timeline) => {
            timeline.pause();
            timeline.kill()
        });
        this._activeBeamIndexes.forEach((id:number) => {
            this.stopIntenseBeam(id);
        });
        this._activeBeamIndexes.length = 0;
        this._activeBeam = -1;
    }

    public resetBeam(reelId:number):void {
        if(this._beamTimelines[reelId]) {
            this._beamTimelines[reelId].pause();
            this._beamTimelines[reelId].kill();
        }
        this.stopIntenseBeam(reelId);
    }
}
